/**
 * help.ts — the (?) help modal for Gold Digger.
 *
 * The architectural description that used to live in the sidebar (the "Agent"
 * block + the footer) lives here now, behind a persistent help button in the
 * top-left corner (same pattern as the fishing-demo). Click the button, the
 * modal mounts on document.body; click outside, click ×, or press Esc to
 * close.
 *
 * Singleton: open is idempotent, close fully tears down (removes the DOM
 * node and the keydown listener).
 */

/** The essay that accompanies this demo — the deep dive on Fitted
 *  Q-Iteration, the deadly triad, and why this agent is built the way it is. */
export const ESSAY_URL = 'https://blog.vski.ai/posts/sequential-recommender/';

export class HelpModal {
  readonly el: HTMLElement;
  private onKey: (e: KeyboardEvent) => void;

  constructor(onClose: () => void) {
    this.el = document.createElement('div');
    this.el.className = 'overlay help-overlay';
    this.el.innerHTML = `
      <div class="overlay-card help-card">
        <button class="help-close" aria-label="Close">×</button>
        <h2>Gold Digger</h2>
        <p class="help-lead">A linear-Q agent that learns to collect gold it can only sense noisily.</p>

        <p>An agent roams a 30×30 grid. It does not see the grid directly — only <em>noisy sensor readings</em> of cells near it (P(correct) = 0.85). Those readings feed a Bayesian <strong>belief map</strong> (gold / stone / unknown per cell, with a recency-decaying trace of where it has been). That belief map <em>is the agent's memory</em>. From it, 11 features describe each of 32 candidate leaps (8 directions × 4 jump lengths {1, 2, 4, 8}); a linear Q-function scores them and the agent picks the highest.</p>

        <p>Q is learned by <strong>Fitted Q-Iteration</strong>: every few episodes the Q-weights are refit by closed-form ridge regression on a replay buffer, bootstrapping against a <em>frozen</em> copy of Q. Ridge regression is the exact minimiser of a convex problem — it <em>cannot diverge</em>. This is the deliberate escape from the <em>deadly triad</em> (off-policy + bootstrapping + function approximation) that blew up the old GRU agent. Action masking (never pick a candidate whose belief-says-blocked path) bakes in "don't ram walls" so the weights learn the interesting trade-off instead.</p>

        <h3>How to read the canvas</h3>
        <ul class="help-legend">
          <li><span class="help-emoji coin"></span> <strong>Gold</strong> — collect these. Rendered as coins.</li>
          <li><span class="help-emoji rock"></span> <strong>Stones</strong> — obstacles the agent must route around. Rendered as rocks. Density randomises 5–15% per episode.</li>
          <li><span class="help-emoji agent"></span> <strong>Agent</strong> — the disc with a heading tick; the ring around it is its sensor radius.</li>
          <li><span class="help-emoji belief"></span> <strong>Belief heat-map</strong> — warm = believed gold, cool = believed stone, only on cells it has sensed.</li>
        </ul>

        <h3>What the metrics mean</h3>
        <table class="metric-table">
          <tr>
            <td class="metric-name">This episode</td>
            <td>Gold collected / total available in the current episode. The layout randomises every episode, so this won't monotonically rise — but the agent's <em>efficiency vs random</em> climbs as Q trains.</td>
          </tr>
          <tr>
            <td class="metric-name">Training round</td>
            <td>How many times Q has been refit. Round 0 collects with ε = 1 (random); later rounds anneal ε down to the floor.</td>
          </tr>
          <tr>
            <td class="metric-name">Replay buffer</td>
            <td>Transitions stored / capacity. Drives each FQI round's ridge solve.</td>
          </tr>
          <tr>
            <td class="metric-name">last |ΔQ|</td>
            <td>Mean absolute weight change in the last fit round. Falls as Q converges.</td>
          </tr>
          <tr>
            <td class="metric-name">Actions (bars)</td>
            <td>The 32 Q-values right now, grouped by leap length (rows 1, 2, 4, 8) × direction (E/W/S/N + 4 diagonals). Normalised across all 32 — the tallest bar is the agent's current pick.</td>
          </tr>
          <tr>
            <td class="metric-name">Return curve</td>
            <td>Per-episode return over the run. Because layouts randomise, watch this as a trend, not a monotone climb.</td>
          </tr>
        </table>

        <h3>Try it</h3>
        <ul class="help-list">
          <li>Paint extra gold or obstacles mid-run — the agent folds them into its belief on the next sensing pass.</li>
          <li>Drop the sensor P(correct) slider to 0.55 — the belief gets noisier and the agent second-guesses itself.</li>
          <li>Raise <em>bump (flat)</em> to −0.05 — the agent's leaps get bolder; lower it to −1 and it crawls.</li>
          <li>Hit <em>Refit Q now</em> to force one FQI round immediately.</li>
        </ul>

        <a class="essay-btn" href="${ESSAY_URL}" target="_blank" rel="noopener">Read the essay →</a>
      </div>
    `;
    // Click on the dimmed backdrop (not the card) closes; so does the × and Esc.
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) onClose();
    });
    this.el.querySelector<HTMLButtonElement>('.help-close')!.addEventListener('click', onClose);
    this.onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', this.onKey);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.el);
  }

  remove(): void {
    document.removeEventListener('keydown', this.onKey);
    this.el.remove();
  }
}
